import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import MovieList from '../components/MovieList';
import CategoryTabs from '../components/CategoryTabs';
import Pagination from '../components/Pagination';
import { movieAPI } from '../services/api';
import type { Movie } from '../stores/movieStore';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('全部');

  const query = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const sort = searchParams.get('sort') || 'relevance';

  const categories = ['全部', '电影', '电视剧', '动漫', '综艺', '纪录片'];
  const sortOptions = [
    { value: 'relevance', label: '综合排序' },
    { value: 'rating', label: '评分最高' },
    { value: 'latest', label: '最新上线' },
    { value: 'popular', label: '最受欢迎' },
  ];

  useEffect(() => {
    if (query) {
      searchMovies();
    } else if (sort) {
      fetchMoviesBySort();
    }
  }, [query, page, sort]);

  const searchMovies = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const response = await movieAPI.search(query, page);
      setMovies(response.data?.movies || []);
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      console.error('Search failed:', error);
      setMovies([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoviesBySort = async () => {
    setLoading(true);
    try {
      const response = sort === 'popular'
        ? await movieAPI.getPopular(page)
        : await movieAPI.getLatest(page);
      setMovies(response.data?.movies || []);
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      console.error('Fetch failed:', error);
      setMovies([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (newQuery: string) => {
    setSearchParams({ q: newQuery, page: '1', sort });
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams({ q: query, page: newPage.toString(), sort });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortChange = (newSort: string) => {
    setSearchParams({ q: query, page: '1', sort: newSort });
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <SearchBar initialQuery={query} onSearch={handleSearch} size="md" />
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <CategoryTabs
            categories={categories}
            activeCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">排序:</span>
            <select
              value={sort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="bg-slate-800/50 text-white text-sm rounded-lg px-3 py-2 border border-slate-700 focus:border-blue-500 focus:outline-none"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {query ? (
          <div className="mb-6">
            <h1 className="text-xl text-gray-300">
              搜索 "<span className="text-white font-medium">{query}</span>" 的结果
            </h1>
            {!loading && (
              <p className="text-gray-500 text-sm mt-1">
                找到 {movies.length} 个相关结果
              </p>
            )}
          </div>
        ) : (
          <div className="mb-6">
            <h1 className="text-xl text-gray-300">
              {sort === 'popular' ? '热门推荐' : '最新上线'}
            </h1>
          </div>
        )}

        <MovieList
          movies={movies}
          loading={loading}
          emptyMessage={query ? `未找到 "${query}" 相关的结果` : '暂无数据'}
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

export default SearchPage;
